import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Segmented } from "../../../components/ui";
import { apiError, previewQuizReward, sendQuizReward } from "../../../services/quizAdmin";

const PREMIUM = [0, 7, 15, 30, 90, 365];

/**
 * Reward or message to one player: Premium days (applied at once), a free-form
 * gift (handed over by the team) and a congratulation or encouragement email
 * (+ push), with the exact email shown before sending.
 */
const RewardModal = ({ player, week, initialTone = "congrats", onClose, onSent }) => {
  const { t } = useTranslation();
  const [tone, setTone] = useState(initialTone);
  const [premiumDays, setPremiumDays] = useState(initialTone === "congrats" && player.rank <= 3 ? 30 : 0);
  const [giftLabel, setGiftLabel] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const body = { userId: player.user.id, week, tone, premiumDays: tone === "congrats" ? premiumDays : 0, giftLabel: tone === "congrats" ? giftLabel : "", message, sendEmail };

  useEffect(() => {
    const timer = setTimeout(() => {
      previewQuizReward(body)
        .then(setPreview)
        .catch((error) => setPreview({ error: apiError(error) || t("Something went wrong") }));
    }, 400);
    return () => clearTimeout(timer);
  }, [tone, premiumDays, giftLabel, message]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setSending(true);
    try {
      const reward = await sendQuizReward(body);
      toast.success(reward.emailSent || !sendEmail ? t("Sent to {{name}}", { name: player.user.name }) : t("Saved, but the email could not be sent"));
      onSent();
    } catch (error) {
      toast.error(apiError(error) || t("Something went wrong"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} centered size="xl" dialogClassName="ui-modal">
      <Modal.Header>
        <Modal.Title>
          {player.user.name}
          {player.rank ? ` · ${player.rank === 1 ? t("1st") : t("{{rank}}th", { rank: player.rank })}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="quiz-reward">
          <div className="quiz-reward-form">
            <Segmented
              label={t("Message")}
              value={tone}
              onChange={setTone}
              options={[
                { value: "congrats", label: t("Congratulate / reward") },
                { value: "encourage", label: t("Encourage") },
              ]}
            />
            {tone === "congrats" && (
              <>
                <label className="ui-label" htmlFor="quiz-premium">
                  {t("Premium days offered")}
                </label>
                <select id="quiz-premium" className="ui-input" value={premiumDays} onChange={(e) => setPremiumDays(Number(e.target.value))}>
                  {PREMIUM.map((d) => (
                    <option key={d} value={d}>
                      {d ? t("{{count}} days of Premium", { count: d }) : t("No Premium days")}
                    </option>
                  ))}
                </select>
                <label className="ui-label" htmlFor="quiz-gift">
                  {t("Other gift (optional)")}
                </label>
                <input id="quiz-gift" className="ui-input" maxLength={140} value={giftLabel} placeholder={t("e.g. Smartphone, 50 € voucher, AFO T-shirt")} onChange={(e) => setGiftLabel(e.target.value)} />
                <p className="ui-hint">{t("Premium days are applied right away. Another gift is only announced: the team hands it over.")}</p>
              </>
            )}
            <label className="ui-label" htmlFor="quiz-message">
              {t("Personal message (optional)")}
            </label>
            <textarea id="quiz-message" className="ui-input" rows={4} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />
            <label className="quiz-reward-check">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} /> {t("Send the email")} ({preview?.to || player.user.email || t("no email")})
            </label>
            <p className="ui-hint">{t("If the player has the app, they also receive a notification.")}</p>
          </div>
          <div className="quiz-reward-preview">
            {preview?.error ? (
              <p className="ui-hint">{preview.error}</p>
            ) : preview ? (
              <>
                <p className="ui-media-meta">
                  {t("Subject")} : <strong>{preview.subject}</strong>
                </p>
                <iframe title={t("Preview")} className="ledger-preview-frame" srcDoc={preview.html} sandbox="" />
              </>
            ) : (
              <p className="ui-hint">{t("Preparing the preview…")}</p>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
          {t("Cancel")}
        </button>
        <button type="button" className="ui-btn" disabled={sending} onClick={submit}>
          <i className={tone === "congrats" ? "ri-award-line" : "ri-mail-send-line"} aria-hidden="true"></i>
          {sending ? t("Sending…") : tone === "congrats" ? t("Reward and send") : t("Send the encouragement")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default RewardModal;
